// Temporary copy of useGetUrlContexts from @elation/api-client/src/stitch-api-mock/stitchAPI
// (temporarily stored here after deleting stitch-api-mock)

import { customInstance, type ErrorType } from "@elation/api-client/src/core";
import type {
  GetUrlContextsParams,
  UrlContextsResponse,
} from "@src/types/chrome-extension";
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

// biome-ignore lint/suspicious/noExplicitAny: Any needs to be here
type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * Returns URL patterns that let the client detect when a user is viewing
 * a specific model in the remote EHR application and extract relevant IDs.
 * Used for UI state detection in browser extensions.
 *
 * @summary Get URL patterns that indicate which model a user is viewing
 */
export const getUrlContexts = (
  params: GetUrlContextsParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<UrlContextsResponse>(
    {
      url: `/gw/stitch-api-mock/integrations/url-contexts`,
      method: "GET",
      params,
      signal,
    },
    options,
  );
};

export const getGetUrlContextsQueryKey = (params: GetUrlContextsParams) => {
  return [
    `/gw/stitch-api-mock/integrations/url-contexts`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetUrlContextsQueryOptions = <
  TData = Awaited<ReturnType<typeof getUrlContexts>>,
  TError = ErrorType<unknown>,
>(
  params: GetUrlContextsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUrlContexts>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUrlContextsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUrlContexts>>> = ({
    signal,
  }) => getUrlContexts(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUrlContexts>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetUrlContextsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUrlContexts>>
>;
export type GetUrlContextsQueryError = ErrorType<unknown>;

/**
 * @summary Get URL patterns that indicate which model a user is viewing
 */
export const useGetUrlContexts = <
  TData = Awaited<ReturnType<typeof getUrlContexts>>,
  TError = ErrorType<unknown>,
>(
  params: GetUrlContextsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUrlContexts>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetUrlContextsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
